import { Alert, Box, Chip, Stack, Typography } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { isNil } from "lodash";
import { type ReactNode } from "react";

import { type NotificationCutoverConfigFieldsFragment } from "../../generated/graphql";
import {
  deliveryLabel,
  fullAvailabilityText,
  humanizeEnumValue,
  inertFullWarningText,
  isInertFull,
} from "./notification-cutover-format";

const styles = {
  panel: { px: 3, py: 2, maxWidth: 900 },
  sectionHeading: { textTransform: "uppercase", letterSpacing: 0.6 },
  chipRow: { flexWrap: "wrap", gap: 0.75 },
  targetBlock: { mb: 1 },
  noScreen: { fontStyle: "italic" },
  alert: { mb: 2 },
} satisfies Record<string, SxProps<Theme>>;

/** Text used wherever a configuration switch has no screen behind it. */
const NO_SCREEN_TEXT =
  "No screen. Reachable only through an API mutation or SQL.";

type SectionProps = {
  readonly heading: string;
  readonly children: ReactNode;
};

const Section = ({ heading, children }: SectionProps) => (
  <Box>
    <Typography
      color="text.secondary"
      variant="caption"
      sx={styles.sectionHeading}
    >
      {heading}
    </Typography>
    {children}
  </Box>
);

type AudienceListProps = {
  readonly targets: NotificationCutoverConfigFieldsFragment["metadata"]["engineTargets"];
};

/** One audience per block: its operator-facing name and what it means. */
const AudienceList = ({ targets }: AudienceListProps) => (
  <>
    {targets.map((target) => (
      <Box key={target.target} sx={styles.targetBlock}>
        <Typography variant="body2" fontWeight={500}>
          {humanizeEnumValue(target.target)}
          {isNil(target.recipientKind)
            ? ""
            : ` (${humanizeEnumValue(target.recipientKind)})`}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {target.definition}
        </Typography>
      </Box>
    ))}
  </>
);

type NotificationCutoverDetailsProps = {
  readonly config: NotificationCutoverConfigFieldsFragment;
};

/**
 * The expanded detail panel for one notification event: what it is, who it
 * reaches, on which channels, where it is configured, and whether any of that
 * results in a delivery today.
 *
 * Everything here is static metadata resolved server-side from the event
 * registry. No live recipient is resolved, deliberately: doing so would run a
 * recipient-resolution query per event on every page load.
 */
const NotificationCutoverDetails = ({
  config,
}: NotificationCutoverDetailsProps) => {
  const { metadata } = config;

  return (
    <Box sx={styles.panel}>
      {isInertFull(config) ? (
        <Alert severity="warning" sx={styles.alert}>
          {inertFullWarningText(config, config.metadata.deliveryState)}
        </Alert>
      ) : null}

      <Stack spacing={2}>
        <Section heading="What it is">
          <Typography variant="body2">{metadata.description}</Typography>
        </Section>

        <Section heading="What fires it">
          <Typography variant="body2">{metadata.trigger}</Typography>
          <Typography color="text.secondary" variant="body2">
            {isNil(metadata.emittedBy)
              ? "Nothing raises this event yet, so no mode has any effect on it."
              : `Raised by ${metadata.emittedBy}.`}
          </Typography>
        </Section>

        {/* The audience this row controls, kept apart from the one it does not:
            flipping the mode changes only who the engine reaches. */}
        <Section heading="Who the engine reaches if this row is set to Full">
          <AudienceList targets={metadata.engineTargets} />
        </Section>

        {metadata.legacyTargets.length === 0 ? null : (
          <Section heading="Who a legacy path reaches today, whatever this row says">
            <AudienceList targets={metadata.legacyTargets} />
            <Typography color="text.secondary" variant="caption">
              Reached by imperative code outside the engine. Setting this row to
              Full does not necessarily stop these sends; check the event
              description for which of them a cutover suppresses.
            </Typography>
          </Section>
        )}

        <Section heading="Channels the engine can send on">
          {metadata.channels.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              None. This event reaches no channel sender at all.
            </Typography>
          ) : (
            <Stack direction="row" sx={styles.chipRow}>
              {metadata.channels.map((channel) => (
                <Chip
                  key={channel}
                  size="small"
                  variant="outlined"
                  label={
                    config.engineOnlyChannels.includes(channel)
                      ? `${humanizeEnumValue(channel)} (no legacy baseline)`
                      : humanizeEnumValue(channel)
                  }
                />
              ))}
            </Stack>
          )}
          <Typography color="text.secondary" variant="caption">
            What the resolvers can produce, not what has been sent. A channel
            marked no legacy baseline cannot be compared by a shadow run.
          </Typography>
        </Section>

        <Section heading="Where it is switched on or off">
          {metadata.configuredIn.map((point) => (
            <Box key={point.setting} sx={styles.targetBlock}>
              <Typography variant="body2">{point.setting}</Typography>
              <Typography
                color="text.secondary"
                variant="body2"
                sx={isNil(point.clickPath) ? styles.noScreen : undefined}
              >
                {point.clickPath ?? NO_SCREEN_TEXT}
              </Typography>
            </Box>
          ))}
        </Section>

        <Section heading="Does it deliver today">
          <Typography variant="body2" fontWeight={500}>
            {deliveryLabel(metadata.deliveryState)}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {metadata.deliveryStateReason}
          </Typography>
        </Section>

        <Section heading="Before flipping to Full">
          <Typography variant="body2">
            {fullAvailabilityText(config)}
          </Typography>
          {isNil(config.requiredPartnerEvent) ? null : (
            <Typography variant="body2">
              {config.requiredPartnerEvent} must be set to Full first, otherwise
              this event creates notifications that nothing ever clears.
            </Typography>
          )}
          <Typography color="text.secondary" variant="body2">
            {config.shadowComparable
              ? "A shadow run can be compared against the legacy path on at least one of the channels above."
              : "No channel of this event has a legacy path to compare a shadow run against, so it cannot earn Full by observation."}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Source domain: {config.source}
          </Typography>
        </Section>
      </Stack>
    </Box>
  );
};

export default NotificationCutoverDetails;
